<template>
  <div
    class="bg-white border-[1px] rounded-lg border-gray-200 flex p-2 px-4"
    :class="{ 'shadow-sm': !noShadow }"
  >
    <router-link v-if="!isBlurred" :to="`/applicant/${userDetails?.id}`" target="_blank" class="flex items-center">
      <div class="w-12 h-12 rounded-full overflow-hidden ring-1 ring-gray-200">
        <img :src="userDetails?.avatar_image" class="w-12 h-12 object-cover" :class="{ 'privacy-blur': isBlurred }" />
      </div>
      <div class="ml-2 flex flex-col justify-center">
        <p class="text-lg leading-none" :class="{ 'privacy-blur': isBlurred }">
          {{ userDetails?.first_name }}
          {{ userDetails?.last_name }}
        </p>
        <p class="text-gray-600 text-sm mt-1" :class="{ 'privacy-blur': isBlurred }">
          {{ userDetails?.email }}
        </p>
      </div>
    </router-link>
    <div v-else class="flex items-center">
      <div class="w-12 h-12 rounded-full overflow-hidden ring-1 ring-gray-200">
        <img :src="userDetails?.avatar_image" class="w-12 h-12 object-cover" :class="{ 'privacy-blur': isBlurred }" />
      </div>
      <div class="ml-2 flex flex-col justify-center">
        <p class="text-lg leading-none" :class="{ 'privacy-blur': isBlurred }">
          {{ userDetails?.first_name }}
          {{ userDetails?.last_name }}
        </p>
        <p class="text-gray-600 text-sm mt-1" :class="{ 'privacy-blur': isBlurred }">
          {{ userDetails?.email }}
        </p>
      </div>
    </div>

    <div class="flex items-center gap-2 ml-auto">
      <a
        v-if="!isBlurred && applicantProfile?.user_profile?.linkedin_url"
        :href="applicantProfile.user_profile.linkedin_url"
        target="_blank"
      >
        <img src="/linkedin_logo.svg" alt="LinkedIn" class="w-6 h-6" />
      </a>
      <a
        v-if="!isBlurred && applicantProfile?.user_profile?.telegram_handle"
        :href="`https://t.me/${applicantProfile.user_profile.telegram_handle}`"
        target="_blank"
      >
        <img src="/telegram_logo.svg" alt="Telegram" class="w-6 h-6" />
      </a>
      <a
        v-if="!isBlurred"
        :href="`https://outtalent.com/profile/${userDetails?.username}`"
        target="_blank"
        class="border-[1px] border-gray-200 rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-50"
      >
        View Profile
      </a>
      <a
        v-if="!isBlurred && isGuide"
        :href="`https://campus.outtalent.com/manage/fellow/${userDetails?.id}`"
        target="_blank"
        class="border-[1px] border-gray-200 rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-50"
      >
        Management Profile
      </a>
      <a
        v-if="!isBlurred && isGuide && resumeUrl"
        :href="resumeUrl"
        target="_blank"
        class="border-[1px] border-gray-200 rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-50"
        :title="resumeName"
      >
        View Resume
      </a>
      <p
        v-else-if="!isBlurred && isGuide && !resumeUrl"
        class="border-[1px] border-gray-200 rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-50 opacity-50"
      >
        No resume on file
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ApplicantProfile } from '@/types/applicantProfile'
import { useAuth } from '@/composables/useAuth'
import { usePrivacy } from '@/composables/usePrivacy'

const { isGuide } = useAuth()
const { isBlurred } = usePrivacy()

interface ResumeFileDetails {
  id: number
  name: string
  display_url: string
  approval_status: 'pending' | 'approved' | 'revision'
}

interface Props {
  userDetails: {
    id?: number
    avatar_image?: string
    first_name?: string
    last_name?: string
    email?: string
    username?: string
    resume_link?: string | null
  } | null
  resumeFileDetails?: ResumeFileDetails | null
  applicantProfile?: ApplicantProfile | null
  noShadow?: boolean
}

const props = defineProps<Props>()

// Prefer the per-application resume file URL; fall back to legacy resume_link.
const resumeUrl = computed(
  () => props.resumeFileDetails?.display_url || props.userDetails?.resume_link || null
)
const resumeName = computed(
  () => props.resumeFileDetails?.name || 'Resume'
)
</script>
